import axios from 'axios'

export const MAX_SECTION_EXPORT_SECONDS = 600
export const SECTION_EXPORT_POLL_INITIAL_MS = 5000
export const SECTION_EXPORT_POLL_MAX_MS = 30000
export const SECTION_EXPORT_POLL_DEADLINE_MS = 10 * 60 * 1000

const RECENT_EXPORT_STORAGE_PREFIX = 'mgc:ceremony-section-exports:v1'
const RECENT_EXPORT_LIMIT = 10
const RECENT_EXPORT_TTL_MS = 7 * 24 * 60 * 60 * 1000

const PROCESSING_STATUSES = new Set(['SUBMITTING', 'SUBMITTED', 'PROGRESSING'])

function exportsEndpoint(path = '') {
  const base = process.env.VUE_APP_BASE_URL || ''
  return `${base}/semantic-search/exports${path}`
}

function highlightsEndpoint(ceremonyId) {
  const base = process.env.VUE_APP_BASE_URL || ''
  return `${base}/semantic-search/highlights/${encodeURIComponent(ceremonyId)}`
}

export function isSectionExportPending(exportState) {
  return PROCESSING_STATUSES.has(String(exportState?.status || '').toUpperCase())
}

export function sectionExportRequestKey({
  ceremonyId,
  sectionId,
  startSeconds,
  endSeconds,
}) {
  return [ceremonyId, sectionId, startSeconds, endSeconds]
    .map(value => encodeURIComponent(String(value)))
    .join(':')
}

export function createSectionExport({
  ceremonyId,
  sectionId,
  label,
  startSeconds,
  endSeconds,
}) {
  const normalizedStart = Number(startSeconds)
  const normalizedEnd = Number(endSeconds)
  if (
    !Number.isFinite(normalizedStart) ||
    !Number.isFinite(normalizedEnd) ||
    normalizedEnd <= normalizedStart
  ) {
    throw new TypeError('This section does not have a valid time range.')
  }

  return axios.post(
    exportsEndpoint(),
    {
      ceremony_id: ceremonyId,
      section_id: String(sectionId),
      label: String(label || 'Ceremony section'),
      start_seconds: normalizedStart,
      end_seconds: normalizedEnd,
    },
    { timeout: 30000 }
  )
}

export function getSectionExport(exportId) {
  return axios.get(exportsEndpoint(`/${encodeURIComponent(exportId)}`), {
    timeout: 30000,
  })
}

function recentExportStorageKey(scope) {
  return `${RECENT_EXPORT_STORAGE_PREFIX}:${encodeURIComponent(scope)}`
}

export function loadRecentSectionExports(scope) {
  if (!scope) return []
  try {
    const saved = JSON.parse(localStorage.getItem(recentExportStorageKey(scope)) || '[]')
    const oldestAllowed = Date.now() - RECENT_EXPORT_TTL_MS
    return (Array.isArray(saved) ? saved : [])
      .filter(item => (
        item
        && item.exportKey
        && item.request?.ceremonyId
        && Number(item.savedAt) >= oldestAllowed
      ))
      .sort((left, right) => Number(right.savedAt) - Number(left.savedAt))
      .slice(0, RECENT_EXPORT_LIMIT)
  } catch (_) {
    return []
  }
}

export function saveRecentSectionExports(scope, exports) {
  if (!scope) return
  const safeExports = (Array.isArray(exports) ? exports : [])
    .filter(item => item?.exportKey && item.request?.ceremonyId)
    .sort((left, right) => Number(right.savedAt) - Number(left.savedAt))
    .slice(0, RECENT_EXPORT_LIMIT)
    .map(item => ({
      exportKey: String(item.exportKey),
      exportId: item.exportId ? String(item.exportId) : '',
      status: String(item.status || ''),
      savedAt: Number(item.savedAt) || Date.now(),
      request: {
        ceremonyId: String(item.request.ceremonyId),
        sectionId: String(item.request.sectionId || ''),
        label: String(item.request.label || 'Ceremony clip'),
        startSeconds: Number(item.request.startSeconds),
        endSeconds: Number(item.request.endSeconds),
      },
    }))
  try {
    localStorage.setItem(recentExportStorageKey(scope), JSON.stringify(safeExports))
  } catch (_) {
    // Exporting still works when browser storage is unavailable or full.
  }
}

export function getCeremonyFilmstrip(ceremonyId, startSeconds, endSeconds) {
  return axios.get(highlightsEndpoint(ceremonyId), {
    params: {
      filmstrip_start_seconds: startSeconds,
      filmstrip_end_seconds: endSeconds,
    },
    timeout: 30000,
  })
}
